import { useEffect, useState } from 'react';
import { LayoutDashboard, Bell, Download, RefreshCw } from 'lucide-react';
import LogIcon from '../icons/LogIcon';
import ThemeToggle from '../ThemeToggle';
import UserMenu from '../UserMenu';
import NotificationPanel from '../NotificationPanel';
import GlobalActivityModal from '../GlobalActivityModal';
import useNotificationStore from '../../store/notificationStore';
import useAuthStore from '../../store/authStore';
import useOrgStore from '../../store/orgStore';
import UndoToast from '../common/UndoToast';

export default function TopHeader() {
  const user = useAuthStore((s) => s.user);
  const org = useOrgStore((s) => s.org);
  const { unread, startPolling, stopPolling, requestBrowserPermission } = useNotificationStore();
  const [panelOpen, setPanelOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const isStandalone = typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    if (!user) return;
    startPolling(15000);
    return () => stopPolling();
  }, [user, startPolling, stopPolling]);

  useEffect(() => {
    if (!user) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    const ask = () => {
      requestBrowserPermission();
      document.removeEventListener('click', ask);
      document.removeEventListener('keydown', ask);
    };
    document.addEventListener('click', ask, { once: true });
    document.addEventListener('keydown', ask, { once: true });
    return () => {
      document.removeEventListener('click', ask);
      document.removeEventListener('keydown', ask);
    };
  }, [user, requestBrowserPermission]);

  const handleBellClick = async () => {
    await requestBrowserPermission();
    setPanelOpen((v) => !v);
  };

  return (
    <header className="h-20 shrink-0 bg-sidebar border-b border-border flex items-center justify-between px-4 z-50 relative">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-3">
          {org?.logo_url ? (
            <img
              src={org.logo_url}
              alt={org?.name || 'Logo'}
              className="w-auto max-w-[200px] object-contain rounded"
              style={{ height: `${org?.logo_size || 50}px` }}
            />
          ) : (
            <LayoutDashboard className="w-5 h-5 text-accent" />
          )}
          {org?.header_name && (
            <span
              className={`text-sm font-bold tracking-tight${!org?.header_name_color ? ' text-accent' : ''}`}
              style={org?.header_name_color ? { color: org.header_name_color } : undefined}
            >
              {org.header_name}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {!isStandalone && (
          <a
            href="/api/v1/org/downloads/desktop"
            download="Arco 1.0.6.exe"
            title="Baixar app desktop"
            className="p-2 rounded hover:bg-white/5 text-text-secondary transition-colors flex items-center"
          >
            <Download className="w-4 h-4" />
          </a>
        )}
        <button
          onClick={() => window.location.reload()}
          title="Recarregar página"
          className="p-2 rounded hover:bg-white/5 text-text-secondary transition-colors flex items-center"
          aria-label="Recarregar página"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <div className="relative">
          <button
            onClick={handleBellClick}
            title="Notificações"
            className="p-2 rounded hover:bg-white/5 text-text-secondary transition-colors relative"
          >
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[9px] leading-none min-w-[14px] h-[14px] rounded-full flex items-center justify-center px-0.5 font-semibold">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
          {panelOpen && <NotificationPanel onClose={() => setPanelOpen(false)} />}
        </div>
        {user && (
          <button
            onClick={() => setActivityOpen(true)}
            title="Registro de atividades"
            className="p-2 rounded hover:bg-white/5 text-text-secondary transition-colors"
          >
            <LogIcon className="w-4 h-4" />
          </button>
        )}
        <div className="w-px h-5 bg-border mx-2" />
        <ThemeToggle />
        <UserMenu />
      </div>
      {activityOpen && <GlobalActivityModal onClose={() => setActivityOpen(false)} />}
      <UndoToast />
    </header>
  );
}
